import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { UserContext } from '../../../../utils/contexts/UserContext';
import { canOpenPage } from '../../../../utils/permissions';
import { isSubjectFolder } from '../../../../utils/folders';
import { QuestionStatusBadge, statusText } from '../../../../components/QuestionList/questionStatus';
import PassageLock from '../../../../components/Passages/PassageLock';
import { passageLockMessage } from '../../../../components/Passages/passageErrors';
import { BookletLinks, bookletLockMessage, linksBooklets, lockingBooklets } from '../../../QuestionPage/review/bookletLock';

// What EditQuestion and EditDraftQuestion show instead of the form (a question the user may not edit, or
// one that did not load) and above it (a save refused for the question's stage, an edit made meanwhile by
// someone else), with the way back.

const focusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600';
// 44px on phones (touch targets), 40px from sm up, like the form's own buttons.
const linkClass = (primary) =>
  `inline-flex h-10 items-center justify-center gap-1.5 rounded-lg px-5 text-sm font-medium transition duration-200 max-sm:h-11 ${focusRing} ${
    primary
      ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700'
      : 'bg-white text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:text-slate-900'
  }`;

// The statuses its author still changes a question of an exam's subject in (the server's AUTHOR_EDITABLE).
const AUTHOR_EDITABLE = ['DRAFT', 'PENDING_REVIEW', 'CHANGES_REQUESTED', 'RESUBMITTED'];

// A save refused for the question's stage or owner (403 edit_locked / draft_not_own; save_draft's
// not_a_draft): the form stays for the user to copy from, its save does not.
export const SAVE_REFUSALS = ['edit_locked', 'draft_not_own', 'not_a_draft'];

export const saveRefusalMessage = (t, code) =>
  code === 'edit_locked' ? t('my_questions.edit_guard.locked_on_save') : t(`errors.${code}`);

// Why the user may not edit a question its detail's actions.edit refuses, as far as the page can tell.
export const editBlockReason = (t, question, user) => {
  const code = question?.status?.code;
  const own = !!user?.uuid && question?.author?.uuid === user.uuid;
  if (code === 'DRAFT' && !own) return t('errors.draft_not_own');
  if (own && isSubjectFolder(question?.folder) && !AUTHOR_EDITABLE.includes(code)) {
    return t('my_questions.edit_guard.author_locked', { status: statusText(t, code, question?.status?.name) });
  }
  return t('my_questions.edit_guard.no_rights');
};

// Ways back from an edit page: the page it was opened from, else the question's page (a draft has none
// yet), and My questions; the home page when neither opens for the user.
export function useEditBackLinks(uuid, { isDraft = false } = {}) {
  const [t] = useTranslation();
  const { user } = useContext(UserContext) || {};
  const from = useLocation().state?.from;
  const links = [];
  if (from || (!isDraft && canOpenPage(user, '/questions'))) {
    links.push({ to: from || `/questions/${uuid}`, label: t('my_questions.edit_guard.back_to_question') });
  }
  if (canOpenPage(user, '/my-questions')) {
    links.push({ to: '/my-questions', label: t('my_questions.edit_guard.back_to_list') });
  }
  if (!links.length) links.push({ to: '/', label: t('buttons.back') });
  return links;
}

// The detail did not load: gone or not given to the user (404, 403), or a failure worth retrying.
export const loadFailure = (t, error) =>
  [403, 404].includes(error?.response?.status)
    ? { title: t('my_questions.edit_guard.not_found_title'), message: t('my_questions.edit_guard.not_found') }
    : {
        title: t('my_questions.edit_guard.load_failed_title'),
        message: t('my_questions.edit_guard.load_failed'),
        retry: true,
      };

const scrollIntoViewOnMount = (ref) => {
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  ref.current?.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
};

// The page in place of the form. `locked`: the question may not be edited (else it did not load);
// `question`: its name and status, when known.
export function EditUnavailable({ locked = false, title, message, question, links = [], onRetry }) {
  const [t] = useTranslation();
  const Icon = locked ? LockClosedIcon : ExclamationTriangleIcon;
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col motion-safe:animate-fade-in">
      <section
        role={locked ? undefined : 'alert'}
        aria-labelledby="edit-unavailable-title"
        className="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-10 text-center shadow-sm"
      >
        <span
          className={`grid h-12 w-12 place-items-center rounded-full ring-1 ring-inset ${
            locked ? 'bg-amber-50 text-amber-600 ring-amber-600/20' : 'bg-red-50 text-red-600 ring-red-600/20'
          }`}
        >
          <Icon className="h-6 w-6" aria-hidden="true" />
        </span>
        <h2 id="edit-unavailable-title" className="text-lg font-semibold tracking-tight text-slate-900">
          {title}
        </h2>
        {question && (question.name || question.status) && (
          <p className="flex max-w-full flex-wrap items-center justify-center gap-2 text-sm">
            {question.name && <span className="min-w-0 truncate font-medium text-slate-700">{question.name}</span>}
            <QuestionStatusBadge status={question.status} />
          </p>
        )}
        <p className="max-w-md text-sm leading-relaxed text-slate-600">{message}</p>
        <div className="mt-2 flex flex-wrap justify-center gap-2">
          {onRetry && (
            <button type="button" onClick={onRetry} className={linkClass(true)}>
              <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
              {t('my_questions.edit_guard.retry')}
            </button>
          )}
          {links.map((link, i) => (
            <Link key={link.to} to={link.to} className={linkClass(!onRetry && i === 0)}>
              {link.label}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

// What the active BTS booklets `booklets` stop for a question they print (its detail's `booklets`: bookletLock.js
// questionBooklets; the server refuses every change of it), then who returns them to draft. Whoever gets them as links
// (`linked`: bookletLock.js linksBooklets) reads no names before the links, as in BookletLockNotice.
const questionLockMessage = (t, booklets, linked) =>
  bookletLockMessage(
    t,
    linked ? 'question_detail.booklet_lock.change_linked' : 'question_detail.booklet_lock.change',
    booklets,
    linked,
    { counted: true },
  );

// A save refused with 409 question_in_booklet, as the page says it above the form: {message, booklets} (`booklets`:
// their links, for whoever returns them to draft). `kind` is what the booklets print: "passage" (the passage the
// questions would join, which takes no new question now) or "question" (the question itself).
export const bookletRefusal = (t, err, user, kind) => {
  const booklets = lockingBooklets(err);
  const linked = linksBooklets(user, booklets);
  return {
    message:
      kind === 'passage' ? passageLockMessage(t, 'passage', booklets, linked) : questionLockMessage(t, booklets, linked),
    booklets: linked ? booklets : [],
  };
};

// The passages' own lock notice (PassageLock.js) on the question forms: active BTS booklets (`booklets`, PassageLock.js
// passageBooklets) print the passage the questions are written for, so no new question can join it now. Right above
// the send button it holds back, `id` names it for that button (aria-describedby). It ends with what the user can do
// meanwhile: keep the work with "Save as draft and exit" (`saveLater`, where that button is) or, before it, write on.
export function PrintedPassageNotice({ booklets, id, saveLater = true }) {
  const [t] = useTranslation();
  return (
    <div id={id}>
      <PassageLock
        booklets={booklets}
        lock="passage"
        extra={
          saveLater
            ? t('passages.draft.save_later', { button: t('my_questions.actions.save_draft_and_exit') })
            : t('my_questions.create.passage_lock_write')
        }
      />
    </div>
  );
}

// The page in place of the form of a question an active BTS booklet prints (`booklets`): no form whose save could
// only fail, but why, who returns the booklet to draft (an admin, who gets the booklets' links) and the way back.
export function PrintedQuestion({ question, booklets, links }) {
  const [t] = useTranslation();
  const { user } = useContext(UserContext) || {};
  const linked = linksBooklets(user, booklets);
  return (
    <EditUnavailable
      locked
      title={t('my_questions.edit_guard.blocked_title')}
      message={
        <>
          {questionLockMessage(t, booklets, linked)}
          {linked && <BookletLinks booklets={booklets} />}
        </>
      }
      question={question}
      links={links}
    />
  );
}

// Above the form: the save was refused (SAVE_REFUSALS), with the way back (a link's `state` goes with it). `title`
// replaces "Changes not saved" (e.g. for questions that were being created).
export function NotSavedNotice({ message, links = [], title }) {
  const [t] = useTranslation();
  const ref = useRef(null);
  // The save button is at the bottom of a long form: bring the reason into view.
  useEffect(() => scrollIntoViewOnMount(ref), []);
  return (
    <div
      ref={ref}
      role="alert"
      className="flex scroll-mt-4 flex-col gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-start gap-3">
        <LockClosedIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold text-red-900">{title || t('my_questions.edit_guard.not_saved_title')}</p>
          <p className="text-sm text-red-800">{message}</p>
        </div>
      </div>
      {links.length > 0 && (
        <div className="flex shrink-0 flex-wrap gap-2">
          {links.map((link) => (
            <Link key={link.to} to={link.to} state={link.state} className={linkClass(false)}>
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// Above the form reloaded after a 409 stale_version: someone else changed the question meanwhile, or its draft
// was sent to review meanwhile (`stale.draftSent`). `onRestore` puts the user's changes the reload replaced back
// into the form; the notice then says so (`stale.restored`).
export function StaleNotice({ stale, onRestore, onClose }) {
  const [t] = useTranslation();
  const ref = useRef(null);
  const closeRef = useRef(null);
  useEffect(() => scrollIntoViewOnMount(ref), []);
  const message = stale?.restored
    ? t('my_questions.edit_guard.changes_restored')
    : t(stale?.draftSent ? 'my_questions.edit_guard.draft_sent_meanwhile' : 'errors.stale_version');
  // The button goes once used: the focus stays in the notice, which now says what was done.
  const restore = () => {
    onRestore();
    closeRef.current?.focus();
  };
  return (
    <div
      ref={ref}
      role="alert"
      className="flex scroll-mt-4 items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3"
    >
      <ArrowPathIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="min-w-0 text-sm text-amber-900">{message}</p>
        {onRestore && (
          <button type="button" onClick={restore} className={`${linkClass(false)} shrink-0 self-start sm:self-auto`}>
            <ArrowUturnLeftIcon className="h-4 w-4" aria-hidden="true" />
            {t('my_questions.edit_guard.restore_changes')}
          </button>
        )}
      </div>
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        aria-label={t('buttons.close')}
        title={t('buttons.close')}
        className={`-m-1 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-amber-700 transition duration-200 hover:bg-amber-100 max-sm:h-11 max-sm:w-11 ${focusRing}`}
      >
        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
      </button>
    </div>
  );
}
